import { CASH_DENOMINATIONS, type CashEntryKind } from '@barso/shared';

/**
 * Entwurf einer Zählung im Blatt „Bargeld zählen“ (U15a): Er liegt nur im Browser (`sessionStorage`, endet mit dem Tab) und
 * überlebt ein Neuladen. So geht eine halb gezählte Kasse nicht verloren, wenn das Netz fehlt und die Seite neu geladen wird.
 * Gespeichert wird erst online; nach „Speichern“, „Abbrechen“ und beim Abmelden wird der Entwurf gelöscht. Jeder Zugriff ist
 * abgesichert: ohne Speicher (privates Fenster, gesperrt) arbeitet das Blatt einfach ohne Entwurf.
 */
export const CASH_DRAFT_PREFIX = 'barso.cash-draft.';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface CashDraft {
  registerId: string;
  kind: CashEntryKind;
  /** Korrektur dieser Zeile, sonst `null`. */
  supersedesId: string | null;
  mode: 'breakdown' | 'amount';
  /** Anzahl je Stückelung (Cent) als Eingabetext. */
  counts: Record<number, string>;
  amount: string;
  note: string;
  when: string;
  savedAt: number;
}

const keyOf = (eventId: string): string => `${CASH_DRAFT_PREFIX}${eventId}`;
const KINDS: readonly string[] = ['float', 'withdrawal', 'count'];

function parse(value: unknown, nowMs: number): CashDraft | null {
  if (typeof value !== 'object' || value === null) return null;
  const draft = value as Record<string, unknown>;
  const counts: Record<number, string> = {};
  if (typeof draft['counts'] !== 'object' || draft['counts'] === null) return null;
  for (const [key, text] of Object.entries(draft['counts'] as Record<string, unknown>)) {
    const cents = Number(key);
    if (typeof text !== 'string' || !(CASH_DENOMINATIONS as readonly number[]).includes(cents)) {
      return null;
    }
    counts[cents] = text;
  }
  if (
    typeof draft['registerId'] !== 'string' ||
    typeof draft['kind'] !== 'string' ||
    !KINDS.includes(draft['kind']) ||
    (draft['supersedesId'] !== null && typeof draft['supersedesId'] !== 'string') ||
    (draft['mode'] !== 'breakdown' && draft['mode'] !== 'amount') ||
    typeof draft['amount'] !== 'string' ||
    typeof draft['note'] !== 'string' ||
    typeof draft['when'] !== 'string' ||
    typeof draft['savedAt'] !== 'number' ||
    nowMs - draft['savedAt'] >= MAX_AGE_MS
  ) {
    return null;
  }
  return {
    registerId: draft['registerId'],
    kind: draft['kind'] as CashEntryKind,
    supersedesId: draft['supersedesId'],
    mode: draft['mode'],
    counts,
    amount: draft['amount'],
    note: draft['note'],
    when: draft['when'],
    savedAt: draft['savedAt'],
  };
}

/** Liest den Entwurf dieses Basars; unlesbare oder abgelaufene werden gelöscht und ergeben `null`. */
export function loadCashDraft(eventId: string, nowMs: number = Date.now()): CashDraft | null {
  try {
    const text = window.sessionStorage.getItem(keyOf(eventId));
    if (text === null) return null;
    const draft = parse(JSON.parse(text) as unknown, nowMs);
    if (draft === null) window.sessionStorage.removeItem(keyOf(eventId));
    return draft;
  } catch {
    return null;
  }
}

export function saveCashDraft(eventId: string, draft: Omit<CashDraft, 'savedAt'>): void {
  try {
    window.sessionStorage.setItem(
      keyOf(eventId),
      JSON.stringify({ ...draft, savedAt: Date.now() }),
    );
  } catch {
    // ohne Speicher entfällt nur der Schutz vor dem Neuladen
  }
}

export function clearCashDraft(eventId: string): void {
  try {
    window.sessionStorage.removeItem(keyOf(eventId));
  } catch {
    // nichts zu löschen
  }
}

/** Beim Abmelden: Zählungen gehören nicht der nächsten Person am Gerät. */
export function clearAllCashDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(CASH_DRAFT_PREFIX) === true) keys.push(key);
    }
    for (const key of keys) window.sessionStorage.removeItem(key);
  } catch {
    // nichts zu löschen
  }
}
